/*
 * Webmail's brand (milestone 6): the brand of the host it's served on (an
 * agency's verified webmail domain, or Hosterr's) until an address is known,
 * then that mailbox's agency (/api/brand?email=, which goes by the domain, so
 * it says nothing about whether a mailbox exists). Applied with the panel's
 * applier (lib/brand.ts): the accent becomes the brand colors, with its
 * contrast rule, and the favicon follows; BrandMark shows the logo.
 */
import { useEffect } from "react";
import { applyBrand, HOSTERR_BRAND, type Brand } from "../lib/brand";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const cache = new Map<string, Promise<Brand | null>>();
let latest = 0;

function load(email: string | null): Promise<Brand | null> {
  const key = email ?? "";
  let p = cache.get(key);
  if (!p) {
    p = fetch(`/api/brand${email ? `?email=${encodeURIComponent(email)}` : ""}`, { credentials: "same-origin", headers: { accept: "application/json" } })
      .then(async (res) => {
        if (!res.ok) return null;
        const j = (await res.json()) as { brand?: Partial<Brand> };
        return j.brand ? ({ ...HOSTERR_BRAND, ...j.brand } as Brand) : null;
      })
      .catch(() => null);
    cache.set(key, p);
    void p.then((b) => b ?? cache.delete(key)); // a failed lookup is tried again next time
  }
  return p;
}

/** Shows the brand for this address's domain, or the host's when there's no address yet. The last call wins. */
export function useWebmailBrand(address: string | null) {
  useEffect(() => {
    const a = address?.trim().toLowerCase() ?? "";
    const n = ++latest;
    void load(EMAIL.test(a) ? a : null).then((b) => {
      if (b && n === latest) applyBrand(b);
    });
  }, [address]);
}
